import { Api, ApiError } from "../api";
import { agentOrderArgs } from "../commissions";
import { h, replace } from "../dom";
import { today } from "../format";
import { navigate } from "../nav";
import type { ActResult, Me } from "../types";
import { plain } from "../words";
import { can, confirmButton, crumbs, outcome } from "./shared";

// Record a Broker/Agent order by hand (record_agent_order, orders.write): a customer's PO placed with the supplier that
// the reader didn't pick up from mail. Tare sends nothing. Prices come from the customer's approved list and the
// supplier's agreed price unless a line gives them; the platform refuses a Direct customer, or a line with no price.

export async function agentOrderForm(api: Api, me: Me): Promise<HTMLElement> {
  if (!can(me, "orders.write")) {
    return h("div", { class: "page" }, h("p", { class: "lede" }, "Recording orders isn't part of your role."));
  }
  const showCost = can(me, "costs.read");
  const result = h("div", { class: "result", "aria-live": "polite" });
  const input = (id: string, attrs: Record<string, string | number> = {}) =>
    h("input", { id, type: "text", autocomplete: "off", maxlength: 60, ...attrs });
  const customer = input("customer", { placeholder: "e.g. Creative", maxlength: 100 });
  const supplier = input("supplier", { placeholder: "e.g. Rusken", maxlength: 100 });
  const poNumber = input("po-number", { placeholder: "Exactly as written", maxlength: 40 });
  const poDate = h("input", { id: "po-date", type: "date", value: today() });
  const wanted = h("input", { id: "wanted", type: "date" });
  const market = input("market", { placeholder: "Only if the supplier has prices in several" });
  const note = input("note", { placeholder: "Optional", maxlength: 500 });
  const field = (el: HTMLElement, text: string) => h("div", { class: "field" }, h("label", { for: el.id }, text), el);

  interface Row { item: HTMLInputElement; qty: HTMLInputElement; price: HTMLInputElement; cost: HTMLInputElement; tr: HTMLTableRowElement }
  const rows: Row[] = [];
  const body = h("tbody");
  const addRow = () => {
    const n = rows.length + 1;
    const r = {
      item: input(`item-${n}`, { "aria-label": `Line ${n}: SKU or the customer's part number` }),
      qty: input(`qty-${n}`, { inputmode: "numeric", "aria-label": `Line ${n}: EA` }),
      price: input(`price-${n}`, { inputmode: "decimal", placeholder: "Agreed", "aria-label": `Line ${n}: Tare's price per EA` }),
      cost: input(`cost-${n}`, { inputmode: "decimal", placeholder: "Agreed", "aria-label": `Line ${n}: supplier's cost per EA` }),
    } as Omit<Row, "tr">;
    const tr = h("tr", null, h("td", { class: "num" }, String(n)), h("td", null, r.item), h("td", { class: "r" }, r.qty),
      h("td", { class: "r" }, r.price), showCost ? h("td", { class: "r" }, r.cost) : null);
    rows.push({ ...r, tr });
    body.appendChild(tr);
  };
  addRow();
  addRow();
  addRow();
  const more = h("button", { type: "button", class: "button button-small" }, "Add a line");
  more.addEventListener("click", () => {
    if (rows.length < 50) addRow();
    rows[rows.length - 1].item.focus();
  });

  const built = () => agentOrderArgs({
    customer: customer.value, supplier: supplier.value, poNumber: poNumber.value, poDate: poDate.value, wanted: wanted.value,
    market: market.value, note: note.value,
    lines: rows.map((r) => ({ item: r.item.value, qty: r.qty.value, price: r.price.value, cost: showCost ? r.cost.value : "" })),
  });
  const record: HTMLButtonElement = confirmButton("Record the order", "Press again to record it", async () => {
    const b = built();
    if (!("args" in b)) return;
    record.disabled = true;
    try {
      const r = await api.tool<ActResult>("record_agent_order", b.args);
      if (r.status === "APPLIED") return navigate(`/commissions?${new URLSearchParams({ customer: customer.value.trim() })}`);
      replace(result, outcome(r, "Recorded."));
    } catch (err) {
      replace(result, h("p", { class: "error" }, plain((err as ApiError).message)));
    }
    record.disabled = false;
  }, "button-primary", () => {
    const b = built();
    if ("missing" in b) replace(result, h("p", { class: "error" }, b.missing));
    else replace(result);
    return "args" in b;
  });

  return h("div", { class: "page" },
    crumbs({ text: "Commissions", href: "/commissions" }, { text: "Record an agent order" }),
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Record an agent order"),
      h("p", { class: "lede" }, "A Broker/Agent customer's PO placed with the supplier, when it didn't reach Tare by mail. " +
        "Nothing is sent to anyone. Prices come from the customer's approved list and the supplier's agreed price unless you give them.")),
    h("div", { class: "panel form" },
      h("div", { class: "filters" }, field(customer, "Customer"), field(supplier, "Supplier"), field(poNumber, "Customer's PO number")),
      h("div", { class: "filters" }, field(poDate, "PO dated"), field(wanted, "Wanted by"), field(market, "Market")),
      h("div", { class: "scroll" }, h("table", { class: "tbl" },
        h("thead", null, h("tr", null, h("th", { scope: "col" }, "Line"), h("th", { scope: "col" }, "SKU (TG-…) or customer's part number"),
          h("th", { scope: "col", class: "r" }, "EA"), h("th", { scope: "col", class: "r" }, "Price / EA"),
          showCost ? h("th", { scope: "col", class: "r" }, "Cost / EA") : null)),
        body)),
      h("div", null, more),
      field(note, "Note"),
      h("div", { class: "row-act" }, record),
      result));
}
